import { useState, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

export enum DocumentValidity {
  GELDIG = "geldig",
  NIET_GELDIG = "niet_geldig",
  ONBEKEND = "onbekend",
}

export enum DocumentConformity {
  CONFORM = "conform",
  NIET_CONFORM = "niet_conform",
  GELDIG_MAAR_NIET_CONFORM = "geldig_maar_niet_conform",
  ONBEKEND = "onbekend",
}

export enum BulkJobStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export interface DocumentAnalysisResult {
  id: number;
  fileLogId: number;
  status: string;
  validity: DocumentValidity;
  conformity: DocumentConformity;
  extractedData: Record<string, any> | null;
  validityIssues: string[] | null;
  conformityIssues: string[] | null;
  aiRawResponse: string | null;
  aiProvider: string | null;
  aiModel: string | null;
  processingTimeMs: number | null;
  confidence: number | null;
  errorMessage: string | null;
  analyzedBy: string | null;
  createdAt: string;
}

export interface BulkAnalysisJob {
  id: number;
  jobType: "acquisition" | "building";
  acquisitionId: number | null;
  buildingId: string | null;
  status: BulkJobStatus;
  totalDocuments: number;
  processedDocuments: number;
  successfulDocuments: number;
  failedDocuments: number;
  skippedDocuments: number;
  fileLogIds: number[];
  errors: Array<{ fileLogId: number; error: string; timestamp: string }> | null;
  startedBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalProcessingTimeMs: number | null;
  createdAt: string;
}

interface UseDocumentAnalysisReturn {
  // Single document analysis
  analyzeDocument: (fileLogId: number) => Promise<DocumentAnalysisResult | null>;
  isAnalyzing: boolean;

  // Bulk analysis
  startBulkAnalysisForAcquisition: (acquisitionId: number) => Promise<BulkAnalysisJob | null>;
  startBulkAnalysisForBuilding: (buildingId: string) => Promise<BulkAnalysisJob | null>;
  getBulkJobStatus: (jobId: number) => Promise<BulkAnalysisJob | null>;
  cancelBulkJob: (jobId: number) => Promise<boolean>;
  currentBulkJob: BulkAnalysisJob | null;
  isBulkProcessing: boolean;

  // Analysis history
  getAnalysisHistory: (fileLogId: number) => Promise<DocumentAnalysisResult[]>;

  // Utility functions
  getValidityBadge: (validity: DocumentValidity | string | null) => { label: string; color: string };
  getConformityBadge: (conformity: DocumentConformity | string | null) => { label: string; color: string };
}

export const useDocumentAnalysis = (): UseDocumentAnalysisReturn => {
  const { session } = useSession();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [currentBulkJob, setCurrentBulkJob] = useState<BulkAnalysisJob | null>(null);

  const getUserId = useCallback(async () => {
    const userId = session?.user?.id;
    if (!userId) {
      throw new Error("Niet ingelogd");
    }
    return userId;
  }, [session]);

  const analyzeDocument = useCallback(
    async (fileLogId: number): Promise<DocumentAnalysisResult | null> => {
      setIsAnalyzing(true);
      try {
        const userId = await getUserId();
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze/${fileLogId}`,
          {
            method: "POST",
            headers: {
              "x-user-id": userId,
            },
          },
          session
        );

        if (response.ok) {
          const result = await response.json();
          toast.success("Document analyse voltooid");
          return result;
        } else {
          const error = await response.json();
          toast.error(error.message || "Fout bij analyseren document");
          return null;
        }
      } catch (error: any) {
        console.error("Error analyzing document:", error);
        toast.error(error.message || "Fout bij analyseren document");
        return null;
      } finally {
        setIsAnalyzing(false);
      }
    },
    [session, getUserId]
  );

  const startBulkAnalysisForAcquisition = useCallback(
    async (acquisitionId: number): Promise<BulkAnalysisJob | null> => {
      setIsBulkProcessing(true);
      try {
        const userId = await getUserId();
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze-bulk/acquisition/${acquisitionId}`,
          {
            method: "POST",
            headers: {
              "x-user-id": userId,
            },
          },
          session
        );

        if (response.ok) {
          const job = await response.json();
          setCurrentBulkJob(job);
          toast.success(`Bulk analyse gestart voor ${job.totalDocuments} documenten`);
          return job;
        } else {
          const error = await response.json();
          toast.error(error.message || "Fout bij starten bulk analyse");
          return null;
        }
      } catch (error: any) {
        console.error("Error starting bulk analysis:", error);
        toast.error(error.message || "Fout bij starten bulk analyse");
        return null;
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [session, getUserId]
  );

  const startBulkAnalysisForBuilding = useCallback(
    async (buildingId: string): Promise<BulkAnalysisJob | null> => {
      setIsBulkProcessing(true);
      try {
        const userId = await getUserId();
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze-bulk/building/${buildingId}`,
          {
            method: "POST",
            headers: {
              "x-user-id": userId,
            },
          },
          session
        );

        if (response.ok) {
          const job = await response.json();
          setCurrentBulkJob(job);
          toast.success(`Bulk analyse gestart voor ${job.totalDocuments} documenten`);
          return job;
        } else {
          const error = await response.json();
          toast.error(error.message || "Fout bij starten bulk analyse");
          return null;
        }
      } catch (error: any) {
        console.error("Error starting bulk analysis:", error);
        toast.error(error.message || "Fout bij starten bulk analyse");
        return null;
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [session, getUserId]
  );

  const getBulkJobStatus = useCallback(
    async (jobId: number): Promise<BulkAnalysisJob | null> => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze-bulk/job/${jobId}`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const job = await response.json();
          setCurrentBulkJob(job);
          return job;
        }
        return null;
      } catch (error) {
        console.error("Error fetching job status:", error);
        return null;
      }
    },
    [session]
  );

  const cancelBulkJob = useCallback(
    async (jobId: number): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze-bulk/job/${jobId}/cancel`,
          { method: "POST" },
          session
        );

        if (response.ok) {
          toast.success("Bulk analyse geannuleerd");
          setCurrentBulkJob(null);
          return true;
        } else {
          toast.error("Fout bij annuleren bulk analyse");
          return false;
        }
      } catch (error) {
        console.error("Error cancelling bulk job:", error);
        toast.error("Fout bij annuleren bulk analyse");
        return false;
      }
    },
    [session]
  );

  const getAnalysisHistory = useCallback(
    async (fileLogId: number): Promise<DocumentAnalysisResult[]> => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/analyze/history/${fileLogId}`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          return await response.json();
        }
        return [];
      } catch (error) {
        console.error("Error fetching analysis history:", error);
        return [];
      }
    },
    [session]
  );

  const getValidityBadge = useCallback(
    (validity: DocumentValidity | string | null): { label: string; color: string } => {
      switch (validity) {
        case DocumentValidity.GELDIG:
        case "geldig":
          return { label: "Geldig", color: "bg-moss-primary text-white" };
        case DocumentValidity.NIET_GELDIG:
        case "niet_geldig":
          return { label: "Niet geldig", color: "bg-brick-primary text-white" };
        case DocumentValidity.ONBEKEND:
        case "onbekend":
        default:
          return { label: "Onbekend", color: "bg-neutral-200 text-neutral-600" };
      }
    },
    []
  );

  const getConformityBadge = useCallback(
    (conformity: DocumentConformity | string | null): { label: string; color: string } => {
      switch (conformity) {
        case DocumentConformity.CONFORM:
        case "conform":
          return { label: "Conform", color: "bg-moss-primary text-white" };
        case DocumentConformity.NIET_CONFORM:
        case "niet_conform":
          return { label: "Niet conform", color: "bg-brick-primary text-white" };
        case DocumentConformity.GELDIG_MAAR_NIET_CONFORM:
        case "geldig_maar_niet_conform":
          return { label: "Geldig maar niet conform", color: "bg-honey-primary text-neutral-800" };
        case DocumentConformity.ONBEKEND:
        case "onbekend":
        default:
          return { label: "Onbekend", color: "bg-neutral-200 text-neutral-600" };
      }
    },
    []
  );

  return {
    analyzeDocument,
    isAnalyzing,
    startBulkAnalysisForAcquisition,
    startBulkAnalysisForBuilding,
    getBulkJobStatus,
    cancelBulkJob,
    currentBulkJob,
    isBulkProcessing,
    getAnalysisHistory,
    getValidityBadge,
    getConformityBadge,
  };
};
